import {api} from '~/common/checkout/api/api';
import type {
  BaseCartQueryArgs,
  CartDiscountQueryArgs,
  CartQueryArgs,
  CartUpdateQueryArgs,
  CheckoutArgs,
  CheckoutResponse,
  PackageResponse,
} from '~/common/checkout/types/api';

const cartApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getPackages: builder.query<Package[], BaseCartQueryArgs>({
      providesTags: ['Packages'],
      query: ({apiVersion, eventId}) => ({
        url: `/${apiVersion}/event/packages/${eventId}`,
        method: 'GET',
      }),
      transformResponse(response: PackageResponse) {
        const data = response.packages;
        return data as Package[];
      },
    }),
    getCart: builder.query<Cart, BaseCartQueryArgs>({
      providesTags: ['Cart'],
      query: ({apiVersion, eventId, checkoutType}) => ({
        url: `/${apiVersion}/${checkoutType}/${eventId}`,
      }),
      async onQueryStarted(arg, {dispatch}) {
        try {
          // get the packages always before cart due to our experiment setup
          if (arg.checkoutType !== 'pro_subscription') {
            await dispatch(cartApi.endpoints.getPackages.initiate(arg));
          }
        } catch (e) {
          evite.error('Failed to fetch packages before cart', e);
        }
      },
    }),
    updateCart: builder.mutation<Cart, CartUpdateQueryArgs>({
      query: ({apiVersion, eventId, checkoutType, transactionId, data}) => ({
        url: `/${apiVersion}/${checkoutType}/${eventId}`,
        method: 'POST',
        body: {
          ...data,
          transactionId,
        },
      }),
      async onQueryStarted({apiVersion, eventId, checkoutType}, {dispatch, queryFulfilled}) {
        try {
          const result = await queryFulfilled;
          const data = result.data as Cart;
          dispatch(
            cartApi.util.updateQueryData(
              'getCart',
              {apiVersion, eventId, checkoutType},
              (draft) => ({...draft, ...data})
            )
          );
        } catch {
          // ignored
        }
      },
    }),
    setDiscount: builder.mutation<Cart, CartDiscountQueryArgs>({
      query: ({apiVersion, eventId, checkoutType, transactionId, data}) => ({
        url: `/${apiVersion}/${checkoutType}/discounts/${eventId}`,
        method: 'POST',
        body: {
          ...data,
          transactionId,
        },
      }),
      async onQueryStarted({apiVersion, eventId, checkoutType, data}, {dispatch, queryFulfilled}) {
        // use optimistic updates to immediately set the coupon code
        const patchResult = dispatch(
          cartApi.util.updateQueryData('getCart', {apiVersion, eventId, checkoutType}, (draft) => ({
            ...draft,
            ...data,
          }))
        );
        try {
          const {data: newCart} = await queryFulfilled;
          dispatch(
            cartApi.util.updateQueryData(
              'getCart',
              {apiVersion, eventId, checkoutType},
              () => newCart
            )
          );
        } catch {
          patchResult.undo();
        }
      },
    }),
    deleteDiscount: builder.mutation<Cart, CartQueryArgs>({
      query: ({apiVersion, eventId, checkoutType, transactionId}) => ({
        url: `/${apiVersion}/${checkoutType}/discounts/${eventId}`,
        method: 'DELETE',
        body: {
          transactionId,
          code: undefined,
        },
      }),
      async onQueryStarted({apiVersion, eventId, checkoutType}, {dispatch, queryFulfilled}) {
        // use optimistic updates to immediately remove the coupon code
        const patchResult = dispatch(
          cartApi.util.updateQueryData('getCart', {apiVersion, eventId, checkoutType}, (draft) => ({
            ...draft,
            code: undefined,
          }))
        );
        try {
          const {data: newCart} = await queryFulfilled;
          dispatch(
            cartApi.util.updateQueryData(
              'getCart',
              {apiVersion, eventId, checkoutType},
              () => newCart
            )
          );
        } catch {
          patchResult.undo();
        }
      },
    }),
    checkout: builder.mutation<CheckoutResponse, CheckoutArgs>({
      query: ({eventId, apiVersion, checkoutType, transactionId, data: body}) => ({
        url: `/${apiVersion}/${checkoutType}/checkout/${eventId}`,
        method: 'POST',
        body: {
          ...body,
          transactionId,
        },
      }),
    }),
  }),
});

export const {
  useGetCartQuery,
  useGetPackagesQuery,
  useSetDiscountMutation,
  useDeleteDiscountMutation,
  useUpdateCartMutation,
  useCheckoutMutation,
} = cartApi;
